import {type ReactNode} from 'react'
import {HgTextLink, type HgTextLinkProps} from '~/design-system/hg/components'

type HgArticleNotesLinkProps = Omit<
  HgTextLinkProps,
  'variant' | 'newTab' | 'iconProps'
>

export type HgArticleAboutAuthorProps = {
  name: string
  bioShort: ReactNode
  bioMedium: ReactNode
  detailPageLink?: Omit<HgArticleNotesLinkProps, 'children'>
  socialLinks?: HgArticleNotesLinkProps[]
}

export const AuthorName = ({
  name,
  detailPageLink,
}: Pick<HgArticleAboutAuthorProps, 'name' | 'detailPageLink'>) => {
  if (detailPageLink) {
    return (
      <HgTextLink variant="underline" className="arcadia-ui-2" {...detailPageLink}>
        {name}
      </HgTextLink>
    )
  }
  return <span className="text-text-default arcadia-ui-2">{name}</span>
}

const AuthorBio = ({bio}: {bio: React.ReactNode}) => (
  <p className="text-text-subdued arcadia-body-4">{bio}</p>
)

const AuthorSocialLinks = ({
  socialLinks,
}: Required<Pick<HgArticleAboutAuthorProps, 'socialLinks'>>) => {
  return (
    <div className="flex flex-row flex-wrap gap-s3 pt-s1">
      {socialLinks.map((linkProps, idx) => {
        return (
          <HgTextLink
            {...linkProps}
            iconProps={undefined}
            className="arcadia-ui-2"
            newTab
            key={idx}
            variant="underline"
          />
        )
      })}
    </div>
  )
}

const HgArticleAboutAuthor = ({authors}: {authors: HgArticleAboutAuthorProps[]}) => {
  const bioExists = authors.some(author => !!author.bioMedium || !!author.bioShort)
  if (!bioExists) {
    return null
  }

  return (
    <div className="flex flex-col border-t border-border-frosted">
      <p className="py-s3 text-text-default arcadia-heading-9">
        About the author{authors.length === 1 ? '' : 's'}
      </p>
      <div className="flex flex-col space-y-s3">
        {authors.map(
          ({name, bioShort, bioMedium, detailPageLink, socialLinks}, idx) => (
            <div key={idx} className="flex flex-col">
              <div className="pb-s1">
                <AuthorName name={name} detailPageLink={detailPageLink} />
              </div>
              {(bioMedium || bioShort) && <AuthorBio bio={bioMedium || bioShort} />}
              {socialLinks && socialLinks.length > 0 && (
                <AuthorSocialLinks socialLinks={socialLinks} />
              )}
            </div>
          )
        )}
      </div>
    </div>
  )
}

export default HgArticleAboutAuthor
